import { WORDMARK_ACCENT_PATHS, WORDMARK_INK_PATHS, WORDMARK_VIEW_BOX } from '@sim/emcn'

const WORDMARK_FILLS = {
  body: 'var(--text-body)',
  'brand-muted': 'var(--text-tertiary)',
  inherit: 'currentColor',
  'muted-inverse': 'var(--text-muted-inverse)',
} as const

/**
 * omnyqra: heights only, the width follows the Omnyqra logo's aspect ratio, and
 * the nav mark sits on the baseline without Sim's optical offset (Omnyqra/sim#14),
 * see FORK.md.
 */
const WORDMARK_SIZES = {
  nav: { height: 18, className: 'h-[18px] w-auto' },
  tag: { height: 10, className: 'h-[10px] w-auto' },
  loading: { height: 32, className: 'h-8 w-auto' },
} as const

export interface SimWordmarkProps {
  /** Navbar, compact 20px ChipTag, or centered application loading mark. */
  size?: keyof typeof WORDMARK_SIZES
  /** Body ink, inherited foreground, muted brand ink, or light ink for inverse surfaces. */
  tone?: keyof typeof WORDMARK_FILLS
}

/**
 * Canonical logotype, shared by browser and bundled desktop pages.
 *
 * omnyqra: draws the full Omnyqra logo instead of Sim's (Omnyqra/sim#14), see
 * FORK.md. The `body` tone keeps the brand colors (mark and Y/A in Iris, the
 * lettering in the tone ink); every other tone is single-color, as the kit
 * prescribes for monochrome use.
 */
export function SimWordmark({ size = 'nav', tone = 'body' }: SimWordmarkProps) {
  const { height, className } = WORDMARK_SIZES[size]
  const width = Math.round((height * WORDMARK_VIEW_BOX.width) / WORDMARK_VIEW_BOX.height)
  const fill = WORDMARK_FILLS[tone]
  /** Iris fallback: the bundled desktop pages do not load the app's token sheet. */
  const accentFill = tone === 'body' ? 'var(--brand, #6655cc)' : fill

  return (
    <svg
      viewBox={`0 0 ${WORDMARK_VIEW_BOX.width} ${WORDMARK_VIEW_BOX.height}`}
      width={width}
      height={height}
      fill='none'
      aria-hidden='true'
      className={className}
    >
      <g fill={accentFill}>
        {WORDMARK_ACCENT_PATHS.map((d) => (
          <path key={d} d={d} />
        ))}
      </g>
      <g fill={fill}>
        {WORDMARK_INK_PATHS.map((d) => (
          <path key={d} d={d} />
        ))}
      </g>
    </svg>
  )
}
